// Service for bodyweight-related API calls
import { fetchWithAuth } from '../../utils/api';
import { API_URL } from '../../utils';

export interface BodyWeightData {
  weight?: number;
  fat_percentage?: number | null;
  waist_circumference?: number | null;
}

export class BodyWeightApiService {
  /**
   * Fetch bodyweight data for a specific date
   * @param dateKey - Date in YYYY-MM-DD format
   * @returns Bodyweight data or null if no data exists
   */
  static async getBodyWeightForDate(dateKey: string): Promise<BodyWeightData | null> {
    const response = await fetchWithAuth(`${API_URL}/bodyweight?date=${dateKey}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch bodyweight: ${response.status}`);
    }
    const data = await response.json();
    return data || null;
  }

  /**
   * Log bodyweight for a specific date
   * @param dateKey - Date in YYYY-MM-DD format
   * @param data - Bodyweight data to log
   * @returns Response from the API
   */
  static async logBodyWeight(
    dateKey: string,
    data: {
      weight: number;
      fat_percentage?: number;
      waist_circumference?: number;
    }
  ): Promise<Response> {
    const requestBody: any = {
      date: dateKey,
      weight: data.weight
    };

    // Only include fat_percentage if provided
    if (data.fat_percentage !== undefined) {
      requestBody.fat_percentage = data.fat_percentage;
    }

    // Only include waist_circumference if provided
    if (data.waist_circumference !== undefined) {
      requestBody.waist_circumference = data.waist_circumference;
    }

    const response = await fetchWithAuth(`${API_URL}/bodyweight`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      let errorMsg = `Failed to log body weight (status: ${response.status})`;
      try {
        const errorData = await response.json();
        errorMsg = errorData.message || errorMsg;
      } catch (e) {
        // Response might not be JSON
      }
      throw new Error(errorMsg);
    }

    return response;
  }

  /**
   * Fetch the most recent body weight entry before a given date
   * @param dateKey - Date in YYYY-MM-DD format
   * @returns Object with weight or null if no prior entry
   */
  static async getPreviousBodyWeight(dateKey: string): Promise<{ weight: number } | null> {
    const response = await fetchWithAuth(`${API_URL}/bodyweight/previous?date=${dateKey}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch previous bodyweight: ${response.status}`);
    }
    const data = await response.json();
    return data || null;
  }

  /**
   * Fetch all dates with bodyweight data for a specific month
   * @param year - Year (e.g., 2026)
   * @param month - Month number (1-12)
   * @returns Array of date strings in YYYY-MM-DD format
   */
  static async getBodyWeightDates(year: number, month: number): Promise<string[]> {
    const monthStr = String(month).padStart(2, '0');
    const response = await fetchWithAuth(`${API_URL}/bodyweight/dates?month=${year}-${monthStr}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch bodyweight dates: ${response.status}`);
    }
    const dates = await response.json();
    return Array.isArray(dates) ? dates : [];
  }
}
